import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from './common/Auth';
import { firestore } from './common/firebase';
import { collection, doc, addDoc, updateDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import {
	getTimezoneOptions,
	DEFAULT_TIMEZONE,
	parseCoordinate,
	formatCoordinateInput,
} from './systemSettingsUtils';
import {
	hasAstroLocation,
	getAstroTimesForDisplay,
	getPrimaryAstroBanner,
	getSystemTimezone,
} from './astroUtils';
import {
	canUseAiHelp,
	normalizeEventClassPriority,
	parseJulesTime,
} from './aiHelp/entitlement';
import JulesSystemSettings from './aiHelp/JulesSystemSettings';

const EMPTY_VALUES = {
	name: 'New System',
	location: '',
	timezone: DEFAULT_TIMEZONE,
	latitude: '',
	longitude: '',
	aiHelpEnabled: false,
	letJulesDecide: false,
	julesOnTime: '18:00',
	julesOffTime: '23:00',
	julesEventClassPriority: [],
};

function systemToFormValues(system) {
	if (!system) return { ...EMPTY_VALUES };
	return {
		name: system.name || '',
		location: system.location || '',
		timezone: system.timezone || DEFAULT_TIMEZONE,
		latitude: formatCoordinateInput(system.latitude),
		longitude: formatCoordinateInput(system.longitude),
		aiHelpEnabled: system.aiHelpEnabled === true,
		letJulesDecide: system.letJulesDecide === true,
		julesOnTime: system.julesOnTime || '18:00',
		julesOffTime: system.julesOffTime || '23:00',
		julesEventClassPriority: normalizeEventClassPriority(system.julesEventClassPriority),
	};
}

function buildSavePayload(values, isUpdate, includeJules) {
	const payload = {
		name: values.name || '',
		location: values.location || '',
		timezone: values.timezone || DEFAULT_TIMEZONE,
	};

	const latitude = parseCoordinate(values.latitude, -90, 90);
	const longitude = parseCoordinate(values.longitude, -180, 180);

	if (latitude != null) {
		payload.latitude = latitude;
	} else if (isUpdate) {
		payload.latitude = deleteField();
	}

	if (longitude != null) {
		payload.longitude = longitude;
	} else if (isUpdate) {
		payload.longitude = deleteField();
	}

	if (includeJules) {
		const aiHelpEnabled = values.aiHelpEnabled === true;
		const onTime = parseJulesTime(values.julesOnTime) ? values.julesOnTime : '18:00';
		const offTime = parseJulesTime(values.julesOffTime) ? values.julesOffTime : '23:00';
		payload.aiHelpEnabled = aiHelpEnabled;
		payload.letJulesDecide = aiHelpEnabled && values.letJulesDecide === true;
		payload.julesOnTime = onTime;
		payload.julesOffTime = offTime;
		payload.julesEventClassPriority = normalizeEventClassPriority(values.julesEventClassPriority);
	}

	return payload;
}

const SystemEditor = ({ open, onClose, system, newsystem, initialTab = 0 }) => {
	const currentUser = useAuth();
	const [tab, setTab] = useState(initialTab);
	const [values, setValues] = useState({ ...EMPTY_VALUES });
	const [invite, setInvite] = useState({ name: '', email: '' });
	const [permissions, setPermissions] = useState({ write: true, add: true, invite: false, delete: false, control: true });

	const timezoneOptions = useMemo(() => getTimezoneOptions(), []);
	const showJules = canUseAiHelp(currentUser);

	const previewSystem = useMemo(() => ({
		timezone: values.timezone || DEFAULT_TIMEZONE,
		latitude: parseCoordinate(values.latitude, -90, 90),
		longitude: parseCoordinate(values.longitude, -180, 180),
	}), [values.timezone, values.latitude, values.longitude]);

	const todaySunTimes = useMemo(() => {
		if (!hasAstroLocation(previewSystem)) return '';
		const times = getAstroTimesForDisplay(previewSystem, new Date());
		return getPrimaryAstroBanner(times, getSystemTimezone(previewSystem));
	}, [previewSystem]);

	useEffect(() => {
		if (open) setTab(initialTab);
	}, [open, initialTab]);

	useEffect(() => {
		if (system) setValues(systemToFormValues(system));
		else if (newsystem) setValues({ ...EMPTY_VALUES });
	}, [system, newsystem]);

	const genInviteCode = () => {
		return Math.floor(100000 + Math.random() * 900000) + '-' + Date.now().toString(36).slice(-4);
	};

	const save = async () => {
		const isUpdate = !newsystem && !!system?.uid;
		const payload = buildSavePayload(values, isUpdate, showJules);

		if (newsystem || !system?.uid) {
			await addDoc(collection(firestore, 'systems'), {
				...payload,
				userId: currentUser?.uid,
				createdAt: serverTimestamp(),
			});
		} else {
			await updateDoc(doc(firestore, 'systems', system.uid), payload);
		}
		onClose && onClose();
	};

	const sendInvite = async () => {
		if (!invite.email || !system?.uid) return;
		const code = genInviteCode();
		const guestDoc = {
			...invite,
			invitor: currentUser.uid,
			system: system.uid,
			permissions,
			inviteID: code,
			invitee: 'pending',
			createdAt: serverTimestamp(),
		};
		await addDoc(collection(firestore, 'guests'), guestDoc);
		await addDoc(collection(firestore, 'mail'), {
			to: invite.email,
			template: {
				name: 'guestInvitation',
				data: {
					sender: currentUser.first + ' ' + currentUser.last,
					name: invite.name,
					invitecode: code,
					sysname: system.name,
					syslocation: system.location || '',
				},
			},
		});
		alert(`Invitation sent with code: ${code}`);
		setInvite({ name: '', email: '' });
		setTab(0);
		onClose && onClose();
	};

	if (!open) return null;

	return (
		<div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
			<div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-md max-h-[92vh] overflow-auto">
				<div className="flex border-b dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-900 z-10">
					<button onClick={() => setTab(0)} className={`flex-1 py-3 text-sm font-medium ${tab === 0 ? 'text-[#880e4f] border-b-2 border-[#880e4f]' : 'text-gray-500 dark:text-gray-400'}`}>
						System
					</button>
					{!newsystem && (
						<button onClick={() => setTab(1)} className={`flex-1 py-3 text-sm font-medium ${tab === 1 ? 'text-[#880e4f] border-b-2 border-[#880e4f]' : 'text-gray-500 dark:text-gray-400'}`}>
							Invite Guest
						</button>
					)}
				</div>

				<div className="p-4 sm:p-6">
					{tab === 0 && (
						<div className="space-y-4">
							<div>
								<label className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1.5">Name</label>
								<input className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2 w-full" value={values.name || ''} onChange={(e) => setValues({ ...values, name: e.target.value })} placeholder="System name" />
							</div>
							<div>
								<label className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1.5">Location</label>
								<input className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2 w-full" value={values.location || ''} onChange={(e) => setValues({ ...values, location: e.target.value })} placeholder="City, venue, or description" />
							</div>
							<div>
								<label className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1.5">Timezone</label>
								<select
									className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2 w-full text-sm"
									value={values.timezone || DEFAULT_TIMEZONE}
									onChange={(e) => setValues({ ...values, timezone: e.target.value })}
								>
									{timezoneOptions.map((tz) => (
										<option key={tz} value={tz}>{tz}</option>
									))}
								</select>
								<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Used by the scheduler for schedule times.</p>
							</div>
							<div className="grid grid-cols-2 gap-3">
								<div>
									<label className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1.5">Latitude</label>
									<input
										type="number"
										step="any"
										min="-90"
										max="90"
										className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2 w-full text-sm"
										value={values.latitude}
										onChange={(e) => setValues({ ...values, latitude: e.target.value })}
										placeholder="e.g. 53.5461"
									/>
								</div>
								<div>
									<label className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1.5">Longitude</label>
									<input
										type="number"
										step="any"
										min="-180"
										max="180"
										className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2 w-full text-sm"
										value={values.longitude}
										onChange={(e) => setValues({ ...values, longitude: e.target.value })}
										placeholder="e.g. -113.4938"
									/>
								</div>
							</div>
							<p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
								Required for astronomical schedules. Decimal degrees (−90…90 lat, −180…180 lng).
							</p>
							{todaySunTimes && (
								<p className="text-xs text-gray-600 dark:text-gray-400">
									Today: {todaySunTimes}
								</p>
							)}
							{showJules && (
								<JulesSystemSettings
									values={values}
									previewSystem={previewSystem}
									onChange={(patch) => setValues((prev) => ({ ...prev, ...patch }))}
								/>
							)}
							<div className="flex justify-end gap-3 pt-4">
								<button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">Cancel</button>
								<button onClick={save} className="px-4 py-2 text-sm bg-[#880e4f] text-white rounded-lg hover:bg-[#6b0b3f]">{newsystem ? 'Create' : 'Save'}</button>
							</div>
						</div>
					)}

					{tab === 1 && (
						<div className="space-y-4">
							<div>
								<label className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1.5">Guest Name</label>
								<input className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2 w-full" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} placeholder="Guest name" />
							</div>
							<div>
								<label className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1.5">Email</label>
								<input type="email" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2 w-full" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} placeholder="guest@example.com" />
							</div>

							<div>
								<label className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-2">Permissions</label>
								<div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
									{Object.keys(permissions).map((key) => (
										<label key={key} className="flex items-center gap-2">
											<input type="checkbox" checked={permissions[key]} onChange={(e) => setPermissions({ ...permissions, [key]: e.target.checked })} />
											<span className="capitalize">{key}</span>
										</label>
									))}
								</div>
							</div>

							<div className="flex justify-end gap-3 pt-4">
								<button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">Cancel</button>
								<button onClick={sendInvite} disabled={!invite.email} className="px-4 py-2 text-sm bg-[#880e4f] text-white rounded-lg hover:bg-[#6b0b3f] disabled:opacity-50">Send Invite</button>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default SystemEditor;
